/**
 * Khối "Chia việc trang điểm" trong phần Phân công Ekip (quyết định 0287,
 * người chốt 03/10/2026: "phân công cụ thể 1 makeup sẽ make cho từng đối tượng
 * nào trong đơn", chọn "người phân ê-kíp gán sẵn, thợ sửa được, trưởng cửa hàng
 * duyệt" và "theo số lượng từng loại").
 *
 * Mỗi thợ trang điểm của buổi một hàng ba ô Nữ / Nam / Bé dưới 12. Dòng cuối so
 * tổng từng loại cả ê-kíp đã chia với số đơn khai — vượt thì báo đỏ ngay và
 * khoá nút Lưu; máy chủ vẫn là rào thật.
 *
 * Chỉ hiện khi buổi ghi sổ số mặt (`facesLog`) và có ít nhất một thợ trang
 * điểm. KHÔNG hiện một đồng mức công nào — mức nằm ở Cơ chế lương.
 *
 * Đơn chưa khai nữ / nam / bé thì khối mở ô khai của ĐƠN ngay tại chỗ
 * (`MakeupSplitDeclare`) thay vì chỉ nhắc "khai ở thông tin đơn" — có loại đơn
 * không màn nào khác có ô đó (lỗi 04/11/2026, đơn doanh nghiệp DN-312).
 */
import { useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Input } from "@/ui/input";
import { useOrderWorkEntries } from "@/features/orders/quote/api";
import { useAssignMakeupSplit, type MakeupFaceSplit } from "@/features/ekip-schedule/api";
import { useCanPatchOrder } from "@/features/orders/patchDoor";
import { StaffName } from "@/components/StaffName";
import { MakeupSplitDeclare } from "./MakeupSplitDeclare";

type Key = keyof MakeupFaceSplit;
const KEYS: Key[] = ["female", "male", "child"];
const KEY_LABEL: Record<Key, string> = { female: "Nữ", male: "Nam", child: "Bé" };

// Cùng chữ với thẻ "Duyệt số mặt" và khối thợ tự khai — ba màn nói về cùng
// một lời khai thì phải nói cùng một chữ.
const STATE_LABEL = { pending: "Chờ duyệt", approved: "Đã duyệt", rejected: "Đã trả lại" } as const;
const STATE_BADGE = { pending: "yellow", approved: "green", rejected: "red" } as const;

type Draft = Record<string, Record<Key, string>>;

const toText = (n: number | undefined) => (n === undefined ? "" : String(n));

function parse(raw: string): number | null {
  if (raw.trim() === "") return 0;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 0 && n <= 60 ? n : null;
}

export function MakeupSplitAssign({ orderId, canEdit }: { orderId: string; canEdit: boolean }) {
  const { data, isLoading } = useOrderWorkEntries(orderId);
  const saveM = useAssignMakeupSplit(orderId);
  const canPatch = useCanPatchOrder();
  // `null` = chưa sửa gì ⇒ ô hiện phần chia trên máy chủ. Lưu xong đặt lại.
  const [draft, setDraft] = useState<Draft | null>(null);

  if (isLoading || !data?.facesLog || data.makeupFaces.length === 0) return null;

  const crew = data.makeupFaces;
  const want = data.orderFaces;

  const shown: Draft =
    draft ??
    Object.fromEntries(
      crew.map((l) => [
        l.staffId,
        {
          female: toText(l.split?.female),
          male: toText(l.split?.male),
          child: toText(l.split?.child),
        },
      ]),
    );

  // Phần chia đã đọc thành số; `null` ở một ô = gõ sai.
  const rows = crew.map((l) => {
    const r = shown[l.staffId] ?? { female: "", male: "", child: "" };
    const parsed = { female: parse(r.female), male: parse(r.male), child: parse(r.child) };
    return { line: l, raw: r, parsed };
  });
  const badInput = rows.some((r) => KEYS.some((k) => r.parsed[k] === null));
  const got: MakeupFaceSplit = { female: 0, male: 0, child: 0 };
  for (const r of rows) for (const k of KEYS) got[k] += r.parsed[k] ?? 0;
  const over = want ? KEYS.filter((k) => got[k] > want[k]) : [];

  function edit(staffId: string, k: Key, raw: string) {
    setDraft({ ...shown, [staffId]: { ...(shown[staffId] ?? { female: "", male: "", child: "" }), [k]: raw } });
  }

  function save() {
    const entries = rows.map((r) => ({
      staffId: r.line.staffId,
      split: { female: r.parsed.female ?? 0, male: r.parsed.male ?? 0, child: r.parsed.child ?? 0 },
    }));
    saveM.mutate(entries, {
      onSuccess: (v) => {
        setDraft(null);
        toast.success(
          v.facesReview?.state === "pending" ? "Đã chia việc — chờ duyệt" : "Đã chia việc trang điểm",
        );
      },
      onError: (err: unknown) =>
        toast.error(err instanceof Error ? err.message : "Không lưu được phần chia việc"),
    });
  }

  const review = data.facesReview;

  return (
    <div role="group" aria-label="Chia việc trang điểm" className="space-y-2 rounded-md border bg-card p-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-bold">💄 Chia việc trang điểm</span>
        {review ? <Badge variant={STATE_BADGE[review.state]}>{STATE_LABEL[review.state]}</Badge> : null}
      </div>

      {want ? (
        <div className="text-[11px] text-muted-foreground" data-testid="makeup-split-order">
          Đơn có {want.female} nữ · {want.male} nam · {want.child} bé dưới 12
        </div>
      ) : (
        <MakeupSplitDeclare orderId={orderId} canEdit={canPatch} suggested={data.facesSuggested} />
      )}

      {/* Bảng nhỏ: tên trái, ba ô số phải. Trên điện thoại tên xuống dòng riêng
          để ba ô vẫn đủ rộng cho ngón tay. */}
      <div className="space-y-1.5">
        {rows.map(({ line, raw }) => (
          <div key={line.staffId} className="grid grid-cols-3 items-end gap-1.5 md:grid-cols-[minmax(0,1fr)_repeat(3,4.5rem)]">
            <div className="col-span-3 min-w-0 truncate text-xs font-medium md:col-span-1">
              <StaffName staffId={line.staffId} name={line.staffName || line.staffId} sizePx={18} nameClassName="truncate" />
            </div>
            {KEYS.map((k) => (
              <label key={k} className="min-w-0">
                <span className="block text-[10px] text-muted-foreground">{KEY_LABEL[k]}</span>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={60}
                  step={1}
                  placeholder="0"
                  value={raw[k]}
                  readOnly={!canEdit}
                  aria-label={`${KEY_LABEL[k]} — ${line.staffName || line.staffId}`}
                  onChange={(e) => edit(line.staffId, k, e.target.value)}
                  className="h-8 text-right tabular-nums"
                />
              </label>
            ))}
          </div>
        ))}
      </div>

      <div
        className={`text-[11px] tabular-nums ${over.length || badInput ? "text-[var(--color-danger-text)]" : "text-muted-foreground"}`}
        role={over.length || badInput ? "alert" : undefined}
        data-testid="makeup-split-total"
      >
        {badInput
          ? "Mỗi ô nhập số nguyên từ 0 đến 60"
          : `Đã chia ${got.female} nữ · ${got.male} nam · ${got.child} bé` +
            (over.length
              ? ` — vượt số của đơn ở: ${over.map((k) => KEY_LABEL[k].toLowerCase()).join(", ")}`
              : "")}
      </div>

      {canEdit ? (
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            disabled={draft === null || badInput || over.length > 0 || !want || saveM.isPending}
            onClick={save}
          >
            {saveM.isPending ? "Đang lưu…" : "Lưu phần chia"}
          </Button>
        </div>
      ) : null}

      {review?.state === "rejected" && review.rejectNote ? (
        <div className="rounded border border-[var(--color-danger)]/30 bg-[var(--color-danger-bg)]/50 p-1.5 text-[10px] text-[var(--color-danger-text)]">
          Lý do trả lại: {review.rejectNote}
        </div>
      ) : null}
    </div>
  );
}
